import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  addFaqsNotesDocuments,
  deleteFaqsNotesDocuments,
  fetchFaqsNotesDocumentForAssetByAssetId,
  fetchServiceRequestFaqsNotesDocuments,
} from 'src/services/client';
import {
  DocumentApiData,
  DocumentFormData,
  prepareDocumentForApiUploadToS3,
} from 'src/utils/S3UploadDocumentsUtility';
import { parseFAQData } from 'src/utils/CommonHelpers/commonhelpers';
import {
  applyNotesVisibilityFilter,
  filterOutNonDocuments,
  getEntitiesByType,
  hasHomeOwnerVisibleNote,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/helpers';

export interface UseFaqsNotesDocumentsAPIReturn {
  documents: any[];
  notes: any[];
  faqs: any[];
  hasHomeOwnerVisibleNote: boolean;
  loadingDocs: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  fetchDocuments: (entityId: string) => Promise<void>;
  fetchServiceRequestDocuments: (
    requestId: string,
    token?: string
  ) => Promise<void>;
  saveDocument: (
    document: DocumentFormData,
    entityId: string,
    userId: string
  ) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<boolean>;
  removeDocumentFromState: (documentId: string) => void;
}

interface UseFaqsNotesDocumentsAPIOptions {
  itemData?: string;
  customUploadErrorMessage?: string;
  user?: any;
  token?: any;
}

const sortDocumentsByName = (docs: any[]): any[] =>
  [...docs].sort((a: any, b: any) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, {
      sensitivity: 'base',
    })
  );

export const useDocumentsAPI = ({
  itemData = 'itemdata1',
  customUploadErrorMessage,
  user,
  token,
}: UseFaqsNotesDocumentsAPIOptions = {}): UseFaqsNotesDocumentsAPIReturn => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [hasHomeOwnerNote, setHasHomeOwnerNote] = useState<boolean>(false);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const resetMixedBucketState = (): void => {
    setDocuments([]);
    setNotes([]);
    setFaqs([]);
    setHasHomeOwnerNote(false);
  };

  const processMixedResponse = (rawBucket: any[]) => {
    const parsedDocs: any[] = rawBucket.map((doc: any) => parseFAQData(doc));
    const documentsOnly: any[] = filterOutNonDocuments(parsedDocs);
    const sortedDocuments: any[] = sortDocumentsByName(documentsOnly);

    const rawNotes: any[] = getEntitiesByType(rawBucket, 'notes');
    const visibleNotes: any[] = applyNotesVisibilityFilter(rawNotes, user);

    const faqEntities: any[] = getEntitiesByType(rawBucket, 'faqs');

    setDocuments(sortedDocuments);
    setNotes(visibleNotes);
    setFaqs(faqEntities);
    setHasHomeOwnerNote(hasHomeOwnerVisibleNote(rawNotes));
  };

  // Dedicated Service Request Fetcher.
  const fetchServiceRequestDocuments = async (
    requestId: string
  ): Promise<void> => {
    setLoadingDocs(true);

    try {
      const response: any = await fetchServiceRequestFaqsNotesDocuments(
        requestId,
        token
      );

      if (response.ok || response.status === 200) {
        const rawData = await response.json();
        // SR API returns the array directly, so we pass it straight to the processor.
        processMixedResponse(rawData);
      }
    } catch (error) {
      console.error('Error fetching service request documents:', error);
      resetMixedBucketState();
    } finally {
      setLoadingDocs(false);
    }
  };

  // Standard Asset Fetcher.
  const fetchDocuments = async (entityId: string): Promise<void> => {
    setLoadingDocs(true);

    try {
      const response: any = await fetchFaqsNotesDocumentForAssetByAssetId(
        entityId,
        itemData,
        token
      );

      if (response.ok) {
        const data = await response.json();
        processMixedResponse(data.documents || data || []);
      }
    } catch (error) {
      console.error('Error fetching asset documents:', error);
      resetMixedBucketState();
    } finally {
      setLoadingDocs(false);
    }
  };

  const saveDocument = async (
    document: DocumentFormData,
    entityId: string,
    userId: string
  ): Promise<void> => {
    setIsSaving(true);

    try {
      const apiData: DocumentApiData = await prepareDocumentForApiUploadToS3(
        document,
        entityId,
        userId,
        null,
        token
      );

      if (!apiData) {
        toast.error(
          customUploadErrorMessage ??
            'Failed to prepare this document for upload. Please try again or contact Juniper support if the issue persists.',
          { autoClose: 12000, position: 'top-center' }
        );
        return;
      }

      const response: any = await addFaqsNotesDocuments(apiData, token);

      if (!(response.ok || response.status === 200)) {
        toast.error('Document adding failed, please try again.', {
          autoClose: 12000,
          position: 'top-center',
        });
        return;
      }

      const responsejson = await response.json();
      const savedDoc = parseFAQData(responsejson.data);

      toast.success('ⓘ Document has been added.', {
        autoClose: 10000,
        position: 'top-center',
      });

      setDocuments((prev) => sortDocumentsByName([...prev, savedDoc]));
    } catch (error) {
      toast.error(
        customUploadErrorMessage ??
          'Document upload failed. Please check your connection and try again.',
        { autoClose: 12000, position: 'top-center' }
      );
      console.error('Document upload error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const removeDocumentFromState = (documentId: string): void => {
    setDocuments((prev) => prev.filter((doc: any) => doc.id !== documentId));
  };

  const deleteDocument = async (documentId: string): Promise<boolean> => {
    setIsDeleting(true);

    try {
      const response: any = await deleteFaqsNotesDocuments(documentId);

      if (!(response.ok || response.status === 200)) {
        toast.error('ⓘ   Document deleting failed.', {
          autoClose: 2000,
          position: 'top-center',
        });
        return false;
      }

      toast.success('ⓘ   Document has been deleted.', {
        autoClose: 10000,
        position: 'top-center',
      });

      setDocuments((prev) => prev.filter((doc: any) => doc.id !== documentId));
      return true;
    } catch (error) {
      toast.error('ⓘ   Document deleting failed.', {
        autoClose: 2000,
        position: 'top-center',
      });

      console.error('Delete error:', error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    documents,
    notes,
    faqs,
    hasHomeOwnerVisibleNote: hasHomeOwnerNote,
    loadingDocs,
    isSaving,
    isDeleting,
    fetchDocuments,
    fetchServiceRequestDocuments,
    saveDocument,
    deleteDocument,
    removeDocumentFromState,
  };
};
